"use client"

import { useState, useEffect, useRef } from 'react'
import { Search, Loader2, Tag, Percent, MapPin, Plus, Clock } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import TaxesService from '@/api/taxes.service'

interface Tax {
  id: number
  taxName: string
  taxRate: number
  country?: string
  type?: string
  code?: string
  taxAmount?: number
}

interface QuickAddTax {
  taxName: string
  taxRate: number
  country?: string
}

interface TaxSearchFieldProps {
  onSelect: (tax: Tax) => void
  onQuickAdd?: (tax: QuickAddTax) => Promise<Tax | null>
  selectedTaxes?: Tax[]
  businessId?: number
  className?: string
  placeholder?: string
  showRecent?: boolean
  showQuickAdd?: boolean
  disabled?: boolean
}

export function TaxSearchField({
  onSelect,
  onQuickAdd,
  selectedTaxes = [],
  businessId,
  className,
  placeholder = "Search taxes...",
  showRecent = true,
  showQuickAdd = true,
  disabled = false
}: TaxSearchFieldProps) {
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [searchResults, setSearchResults] = useState<Tax[]>([])
  const [recentTaxes, setRecentTaxes] = useState<Tax[]>([])
  const [defaultTaxes, setDefaultTaxes] = useState<Tax[]>([])
  const [filteredDefaultTaxes, setFilteredDefaultTaxes] = useState<Tax[]>([])
  const [isAddingTax, setIsAddingTax] = useState(false)
  const [searchDebounceTimeout, setSearchDebounceTimeout] = useState<NodeJS.Timeout | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()

  // Common tax rates for quick add
  const quickAddTaxRates: QuickAddTax[] = [
    { taxName: 'Standard VAT', taxRate: 15 },
    { taxName: 'Reduced VAT', taxRate: 5 },
    { taxName: 'Zero Rate', taxRate: 0 },
    { taxName: 'Sales Tax', taxRate: 6 },
    { taxName: 'GST', taxRate: 15 }
  ]

  // Fetch default taxes for the business
  useEffect(() => {
    const fetchDefaultTaxes = async () => {
      if (!businessId) return
      
      try {
        const response = await TaxesService.getBusinessTaxInfos(businessId)
        if (response.data.result === 'success') {
          setDefaultTaxes(response.data.taxes || [])
        }
      } catch (error) {
        console.error('Failed to load default taxes', error)
      }
    }
    
    fetchDefaultTaxes()
  }, [businessId])

  // Fetch recent taxes from localStorage
  useEffect(() => {
    const fetchRecentTaxes = () => {
      if (!showRecent) return
      
      try {
        const storedRecentTaxes = localStorage.getItem('recentTaxes')
        if (storedRecentTaxes) {
          const parsedTaxes = JSON.parse(storedRecentTaxes)
          setRecentTaxes(parsedTaxes)
        }
      } catch (error) {
        console.error('Failed to load recent taxes', error)
        // Ignore localStorage errors
      }
    }
    
    fetchRecentTaxes()
  }, [showRecent])

  // Filter default taxes based on search term
  useEffect(() => {
    if (!inputValue.trim()) {
      setFilteredDefaultTaxes(defaultTaxes)
      return
    }
    
    const lowerCaseSearch = inputValue.toLowerCase()
    const filtered = defaultTaxes.filter(tax => 
      tax.taxName.toLowerCase().includes(lowerCaseSearch) ||
      tax.country?.toLowerCase().includes(lowerCaseSearch) ||
      tax.type?.toLowerCase().includes(lowerCaseSearch) ||
      tax.code?.toLowerCase().includes(lowerCaseSearch) ||
      tax.taxRate.toString().includes(inputValue)
    )
    
    setFilteredDefaultTaxes(filtered)
    
    // Debounce search to avoid too many API calls
    if (searchDebounceTimeout) {
      clearTimeout(searchDebounceTimeout)
    }
    
    const timeoutId = setTimeout(() => {
      handleSearch()
    }, 300)
    
    setSearchDebounceTimeout(timeoutId)
    
    return () => {
      if (searchDebounceTimeout) {
        clearTimeout(searchDebounceTimeout)
      }
    }
  }, [inputValue, defaultTaxes])

  const handleSearch = async () => {
    if (!inputValue.trim()) return
    
    setIsLoading(true)
    try {
      // Simulate API search as we don't have a direct search endpoint
      // In a real implementation, you would call a specific search endpoint
      let response
      
      // Try to search by country first
      response = await TaxesService.getDefaultTaxesByCountry(inputValue)
      console.log("getDefaultTaxesByCountry response.data", response.data);
      if (response.data.result === 'success' && response.data.defaultTaxes?.length > 0) {
        // Map API response to the Tax interface
        const mappedTaxes = response.data.defaultTaxes.map((tax: any) => ({
          id: tax.id,
          taxName: tax.name,
          taxRate: tax.rate,
          country: tax.country,
          type: tax.taxType?.name,
          code: tax.publicId
        }));
        setSearchResults(mappedTaxes)
      } else {
        // If country search fails, fallback to all default taxes
        response = await TaxesService.getAllDefaultTaxes()
        console.log("getAllDefaultTaxes response.data", response.data);
        if (response.data.result === 'success') {
          // Filter taxes locally
          const lowerCaseSearch = inputValue.toLowerCase()
          const filtered = response.data.defaultTaxes.filter((tax: any) => 
            tax.name.toLowerCase().includes(lowerCaseSearch) ||
            tax.country?.toLowerCase().includes(lowerCaseSearch) ||
            tax.taxType?.name.toLowerCase().includes(lowerCaseSearch) ||
            tax.publicId?.toLowerCase().includes(lowerCaseSearch) ||
            tax.rate.toString().includes(inputValue)
          )
          // Map API response to the Tax interface
          const mappedTaxes = filtered.map((tax: any) => ({
            id: tax.id,
            taxName: tax.name,
            taxRate: tax.rate,
            country: tax.country,
            type: tax.taxType?.name,
            code: tax.publicId
          }));
          console.log("getAllDefaultTaxes filtered", mappedTaxes);
          setSearchResults(mappedTaxes)
        }
      }
    } catch (error) {
      console.error('Search failed', error)
      toast({
        title: 'Search failed',
        description: 'Failed to search for taxes. Please try again.',
        variant: 'destructive',
      })
      setSearchResults([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleSelectTax = (tax: Tax) => {
    onSelect(tax)
    
    // Add to recent taxes in localStorage
    if (showRecent) {
      try {
        const updatedRecentTaxes = [
          tax,
          ...recentTaxes.filter(t => t.id !== tax.id).slice(0, 4) // Keep only 5 most recent
        ]
        localStorage.setItem('recentTaxes', JSON.stringify(updatedRecentTaxes))
        setRecentTaxes(updatedRecentTaxes)
      } catch (error) {
        // Ignore localStorage errors
      }
    }
    
    setInputValue("")
    setOpen(false)
  }

  const handleQuickAdd = async (taxInfo: QuickAddTax) => {
    if (!onQuickAdd) return
    
    setIsAddingTax(true)
    try {
      const newTax = await onQuickAdd(taxInfo)
      if (newTax) {
        toast({
          title: 'Tax added',
          description: `${newTax.taxName} (${newTax.taxRate}%) has been added successfully.`,
        })
        handleSelectTax(newTax)
      }
    } catch (error) {
      toast({
        title: 'Failed to add tax',
        description: 'Could not add the custom tax. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsAddingTax(false)
    }
  }

  const handleInputChange = (value: string) => {
    setInputValue(value)
    if (!value.trim()) {
      setSearchResults([])
    }
  }

  console.log("searchResults", searchResults);

  return (
    <div className={cn("relative", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleSearch()
                }
              }}
              placeholder={placeholder}
              className="pl-9"
              disabled={disabled}
            />
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-muted-foreground" />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute inset-y-0 right-0 h-full px-3"
              onClick={() => setOpen(true)}
              disabled={disabled}
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            </Button>
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <Command>
            <CommandInput
              placeholder="Search taxes by name, rate, or country"
              value={inputValue}
              onValueChange={handleInputChange}
            />
            <CommandList>
              <CommandEmpty>
                {isLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="py-6 text-center text-sm">
                    <p>No taxes found</p>
                    {showQuickAdd && onQuickAdd && (
                      <p className="mt-2 text-muted-foreground">
                        Use quick add to create a custom tax
                      </p>
                    )}
                  </div>
                )}
              </CommandEmpty>

              {/* Search Results - Prioritized */}
              {searchResults.length > 0 && (
                <CommandGroup heading="Search Results">
                  {searchResults.map((tax) => (
                    <CommandItem
                      key={`search-${tax.id}`}
                      onSelect={() => handleSelectTax(tax)}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        <Tag className="mr-2 h-4 w-4 text-muted-foreground" />
                        <span>{tax.taxName}</span>
                        {tax.country && (
                          <div className="ml-2 flex items-center text-xs text-muted-foreground">
                            <MapPin className="mr-1 h-3 w-3" />
                            {tax.country}
                          </div>
                        )}
                      </div>
                      <Badge variant="secondary" className="ml-auto flex items-center">
                        <Percent className="mr-1 h-3 w-3" />
                        {tax.taxRate}
                      </Badge>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {/* Default Taxes for Business */}
              {filteredDefaultTaxes.length > 0 && searchResults.length === 0 && (
                <>
                  <CommandGroup heading="Business Default Taxes">
                    {filteredDefaultTaxes.map((tax) => (
                      <CommandItem
                        key={`default-${tax.id}`}
                        onSelect={() => handleSelectTax(tax)}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center">
                          <Tag className="mr-2 h-4 w-4 text-muted-foreground" />
                          <span>{tax.taxName}</span>
                          {tax.country && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              {tax.country}
                            </Badge>
                          )}
                        </div>
                        <Badge variant="secondary" className="ml-auto flex items-center">
                          <Percent className="mr-1 h-3 w-3" />
                          {tax.taxRate}
                        </Badge>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  <CommandSeparator />
                </>
              )}

              {/* Recent Taxes */}
              {showRecent && recentTaxes.length > 0 && (
                <>
                  <CommandGroup heading="Recently Used">
                    {recentTaxes.map((tax) => (
                      <CommandItem
                        key={`recent-${tax.id}`}
                        onSelect={() => handleSelectTax(tax)}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center">
                          <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                          <span>{tax.taxName}</span>
                        </div>
                        <Badge variant="outline" className="ml-auto">
                          {tax.taxRate}%
                        </Badge>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  <CommandSeparator />
                </>
              )}

              {/* Quick Add Section */}
              {showQuickAdd && onQuickAdd && (
                <>
                  <CommandGroup heading="Quick Add">
                    <div className="flex flex-wrap gap-2 p-2">
                      {quickAddTaxRates.map((tax, index) => (
                        <Button
                          key={index}
                          variant="outline"
                          size="sm"
                          className="h-8 gap-1 text-xs"
                          onClick={() => handleQuickAdd(tax)}
                          disabled={isAddingTax}
                        >
                          <Plus className="h-3 w-3" />
                          {tax.taxName} ({tax.taxRate}%)
                        </Button>
                      ))}
                    </div>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      
      {/* Display selected taxes */}
      {selectedTaxes.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selectedTaxes.map((tax) => (
            <Badge key={tax.id} variant="secondary" className="py-1 px-2 flex items-center">
              {tax.taxName}: {tax.taxRate}%
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
} 